import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchApplications, deleteApplication, downloadApplication, fetchApplicationById } from "../../services/api";
import Table from "../../utils/Table";
import ActionButtons from "../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../utils/TableUtils";
import ApplicationModal from "../../components/Modals/ApplicationModal";
import { useAuth } from "../../contexts/AuthContext"; 
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";


function Applications({ isTab = false, onCountChange }) {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const { role } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const pageSize = 10;

  const handleEdit = (applicationData) => {
    setSelectedApplication(applicationData);
    setModalShow(true);
  };

  const refreshAppData = () => {
    setModalShow(false);
    setSelectedApplication(null);
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
    
  }

  const handleDelete = async (rowData) => {
    startLoading();
    try {
      const { applicantId } = rowData;
      if (!applicantId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      const response = await deleteApplication(applicantId);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((applicaton) => applicaton.applicantId !== applicantId)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("Error deleting application", "error");
      }
      console.error("Error deleting application:", error);
    } finally {
      stopLoading();
    }
  };

  const handlDownload = async (rowData) => {
    try {
      const { applicantId } = rowData;
      if (!applicantId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      startLoading();
      const response = await downloadApplication(applicantId);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((applicaton) => applicaton.applicantId !== applicantId)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while deleting the community.", "error");
      }
    } finally {
      stopLoading();
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Date Created",
        accessor: "date",
        Cell: ({ value }) => formatDate(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.date?.toLowerCase() || "";
          const b = rowB.values.date?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Application #",
        accessor: "applicantId",
        Cell: ({ value }) => value?.toString().padStart(2, "0"),
        sortType: (rowA, rowB) => {
          const a = rowA.values.applicantId?.toString().toLowerCase() || "";
          const b = rowB.values.applicantId?.toString().toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Applicant Name",
        accessor: row => `${row.firstName} ${row.lastName}`,
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
        minWidth: 150,
        sortType: (rowA, rowB) => {
          const a = rowA.values.firstName?.toLowerCase() || "";
          const b = rowB.values.firstName?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Address",
        accessor: row => `${row?.location?.address}`,
        Cell: ({ row }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.original.location?.address}
          </div>
        ),
        minWidth: 400,
        sortType: (rowA, rowB) => {
          const a = rowA.values.location?.address?.toLowerCase() || "";
          const b = rowB.values.location?.address?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionButtons 
            onEdit={handleEdit} 
            onDelete={handleDelete} 
            onDownload={handlDownload}
            rowData={row.original}
            showDelete={role === "Admin"}
            additonalBtn={true} 
          />
        ),
      },
    ];
  
    return baseColumns;
  }, [role]);
  
  const fetchData = async (id = null, page = null, filter = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : pageIndex;
      const currentFilter = filter !== null ? filter : filterInput;
      let response;
      if (id) {
        response = await fetchApplicationById(id, currentPage + 1, pageSize, null, currentFilter);
        setData(response.data);
        if (onCountChange && response.pagination) onCountChange(response.pagination.totalCount);
      } else {
        response = await fetchApplications(currentPage + 1, pageSize, null, currentFilter);
        setData(response.data);
        setPageCount(response.pagination.totalPages);
        if (onCountChange && response.pagination) onCountChange(response.pagination.totalCount);
      }
    } catch (error) {
      
      console.error("Error fetching data:", error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 0 when filter changes and fetch data
  useEffect(() => {
    setPageIndex(0);
    if (id) {
      fetchData(id, 0, filterInput);
    } else {
      fetchData(null, 0, filterInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterInput, id]);

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  return (
    <div className={isTab ? "" : "subMain-content"}>
      {!isTab && (
        <div>
          <h1 className="mt-4">Applications</h1>
        </div>
      )}
    <Table
      columns={columns}
      data={data}
      filterInput={filterInput}
      setFilterInput={setFilterInput}
      onPageChange={handlePageChange}
      currentPage={pageIndex}
      pageCount={pageCount}
      loading={loading}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
    />
    {selectedApplication && <ApplicationModal
        show={modalShow}
        data={selectedApplication}
        onHide={refreshAppData}
      />}
    </div>
  );
}

export default Applications;
